import React, { useState, useMemo, useEffect, useRef } from 'react';
import { keyframes } from '@emotion/react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Chip,
  Link,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CallMadeIcon from '@mui/icons-material/CallMade';
import BusinessIcon from '@mui/icons-material/Business';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckIcon from '@mui/icons-material/Check';
import CircleIcon from '@mui/icons-material/Circle';
import EditIcon from '@mui/icons-material/Edit';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import type { WorkItem } from '../workItemTypes';
import type { PartOrderLine } from '@techgitter/contracts';
import { formatCustomerDisplay, formatStatusDisplay, formatSmartDate, formatPhoneCallTooltip, getBillingSummary } from '../utils/workItemUtils';
import { getStatusOptionsForType, getStatusColor } from '../utils/workItemStatusConfig';
import { computeChecklistProgress, hydrateChecklist } from '../../../utils/checklistEngine';
import { WorkItemExpansionPanel } from './WorkItemExpansionPanel';
import { PartsReadyBadge } from '../PartsReadyBadge';

const signalrGlow = keyframes`
  from { background-color: rgba(25, 118, 210, 0.12); }
  to   { background-color: transparent; }
`;

interface WorkItemAccordionRowProps {
  workItem: WorkItem;
  userName: string;
  isAdmin?: boolean;
  onWorkItemUpdated: (id: string, updates: Partial<WorkItem>) => void;
  onStatusChange?: (workItem: WorkItem, newStatus: string) => void;
  onDelete?: (workItem: WorkItem) => Promise<void>;
  onEdit?: (workItem: WorkItem) => void;
  onConvertToJob?: (workItem: WorkItem) => void;
  onPrintTicket?: (workItem: WorkItem) => void;
  onOpenCheckInSession?: (sessionId: string) => void;
  onDragStart?: (e: React.DragEvent, workItemId: string) => void;
  onDragOver?: (e: React.DragEvent, workItemId: string) => void;
  onDrop?: (e: React.DragEvent, workItemId: string) => void;
  onDragEnd?: () => void;
  isDragOver?: boolean;
  isDragging?: boolean;
  isHighlighted?: boolean;
  partLines?: PartOrderLine[];
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function formatCreatedDate(dateStr?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mmm = MONTHS[d.getMonth()];
  const yy = String(d.getFullYear()).slice(-2);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${dd} ${mmm} ${yy} ${hh}:${mm}`;
}

export const WorkItemAccordionRow: React.FC<WorkItemAccordionRowProps> = ({
  workItem,
  userName,
  isAdmin,
  onWorkItemUpdated,
  onStatusChange,
  onDelete,
  onEdit,
  onConvertToJob,
  onPrintTicket,
  onOpenCheckInSession,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragOver,
  isDragging,
  isHighlighted,
  partLines,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(null);

  // Restart the CSS animation each time isHighlighted flips to true by
  // briefly removing and re-adding the animation property.
  const [glowing, setGlowing] = useState(false);
  const prevHighlighted = useRef(false);
  useEffect(() => {
    if (isHighlighted && !prevHighlighted.current) {
      setGlowing(false);
      requestAnimationFrame(() => setGlowing(true));
    }
    prevHighlighted.current = !!isHighlighted;
  }, [isHighlighted]);

  const isResolved = ['resolved', 'closed'].includes((workItem.status ?? '').toLowerCase());
  const isUrgent = workItem.type === 'job' && workItem.jobPriority === '1';
  const isAppointment = workItem.type === 'appointment' || workItem.type === 'job-appointment';
  const apptStatus = workItem.status || workItem.appointmentStatus || '';
  const isCancelledAppointment = isAppointment && apptStatus === 'cancelled';
  const isCompletedAppointment = isAppointment && apptStatus === 'completed';
  const isNoShowAppointment = isAppointment && apptStatus === 'no-show';
  const isOverdueAppointment = isAppointment
    && !isCancelledAppointment && !isCompletedAppointment && !isNoShowAppointment
    && !!workItem.startAt && new Date(workItem.startAt).getTime() < Date.now();

  // Task progress
  let taskLabel: string | null = null;
  let taskColor: 'error' | 'success' = 'success';
  if (workItem.taskList) {
    const live = hydrateChecklist(workItem.taskList);
    const p = computeChecklistProgress(live);
    if (p.total > 0) {
      taskLabel = `Tasks (${p.closed} of ${p.total})`;
      taskColor = p.closed < p.total ? 'error' : 'success';
    }
  }

  // Most recent phone call (array is already newest-first from API)
  const lastCall = useMemo(
    () => workItem.phoneCalls?.[0] ?? null,
    [workItem.phoneCalls],
  );


  return (
    <Box
      sx={{ display: 'flex', alignItems: 'stretch', minWidth: 0 }}
      onDragOver={onDragOver ? (e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; onDragOver(e, workItem.guid); } : undefined}
      onDrop={onDrop ? (e) => { e.stopPropagation(); onDrop(e, workItem.guid); } : undefined}
      onDragEnd={onDragEnd}
    >
      {/* Drag handle — outside the Accordion so MUI ButtonBase cannot interfere */}
      {onDragStart && (
        <Box
          draggable
          onDragStart={(e) => onDragStart(e, workItem.guid)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 0.5,
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
        </Box>
      )}
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      variant="outlined"
      sx={{
        flex: 1,
        minWidth: 0,
        '&:before': { display: 'none' },
        borderLeft: isUrgent ? 4 : isAppointment ? 4 : undefined,
        borderLeftColor: isUrgent ? 'error.main'
          : isOverdueAppointment ? '#ef5350'
          : isCancelledAppointment ? '#bdbdbd'
          : isNoShowAppointment ? '#bdbdbd'
          : isCompletedAppointment ? '#6dcb6d'
          : isAppointment ? '#90caf9'
          : undefined,
        bgcolor: isResolved ? '#e8f5e9'
          : isOverdueAppointment ? '#ffebee'
          : isCancelledAppointment ? '#f5f5f5'
          : isNoShowAppointment ? '#f5f5f5'
          : isCompletedAppointment ? '#e8f5e9'
          : isAppointment ? '#e3f2fd'
          : undefined,
        textDecoration: isCancelledAppointment || isNoShowAppointment ? 'line-through' : undefined,
        opacity: isCancelledAppointment || isNoShowAppointment ? 0.6 : isDragging ? 0.5 : 1,
        outline: isDragging ? '2px solid' : undefined,
        outlineColor: isDragging ? 'primary.main' : undefined,
        boxShadow: isDragging ? '0 0 0 2px rgba(25, 118, 210, 0.25)' : undefined,
        borderTop: isDragOver ? '2px solid' : undefined,
        borderTopColor: isDragOver ? 'primary.main' : undefined,
        animation: glowing ? `${signalrGlow} 2s ease-out` : 'none',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ px: 2, '& .MuiAccordionSummary-content': { my: 1, gap: 1.5, alignItems: 'flex-start', minWidth: 0, overflow: 'hidden' } }}
      >
        {/* Ticket number + date (scheduled time for appointments, created date for jobs) */}
        <Box sx={{ flexShrink: 0, width: 110 }}>
          {isAppointment ? (
            <>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#1565c0' }}>
                {workItem.startAt ? new Date(workItem.startAt).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false }) : 'No time'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {workItem.startAt ? formatCreatedDate(workItem.startAt) : ''}
              </Typography>
            </>
          ) : (
            <>
              {workItem.number ? (
                <Typography variant="body2" sx={{ fontWeight: 700, ...(isResolved && { textDecoration: 'line-through' }) }}>
                  <Link
                    href={`https://techsolvers.repairshopr.com/tickets/${workItem.id}`}
                    target="_blank"
                    rel="noopener"
                    onClick={e => e.stopPropagation()}
                    underline="always"
                    color="inherit"
                  >
                    {workItem.number}
                  </Link>
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 700, ...(isResolved && { textDecoration: 'line-through' }) }}>
                  {workItem.id}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" noWrap>
                {formatCreatedDate(workItem.createdOn)}
              </Typography>
            </>
          )}
        </Box>

        {/* Customer + last phone call */}
        <Box sx={{ width: 235, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {workItem.customerBusinessName && (
              <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
            )}
            <Typography variant="body2" sx={{ fontWeight: 600, ...(isResolved && { textDecoration: 'line-through' }) }} noWrap>
              {formatCustomerDisplay(workItem)}
            </Typography>
          </Box>
          {lastCall && (
            <Tooltip title={formatPhoneCallTooltip(lastCall.type, lastCall.createdAt)} arrow>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, cursor: 'default' }}>
                {lastCall.type === 'inbound'
                  ? <CallReceivedIcon sx={{ fontSize: 12, color: 'success.main' }} />
                  : <CallMadeIcon sx={{ fontSize: 12, color: 'info.main' }} />
                }
                <Typography variant="caption" color="text.secondary" noWrap>
                  {formatSmartDate(lastCall.createdAt)}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Title + last job log */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600, ...(isResolved && { textDecoration: 'line-through' }) }}>
            {workItem.title}
          </Typography>
          {workItem.lastJobLog && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
                {workItem.lastJobLog.text}
              </Typography>
              {workItem.lastJobLog.createdAt && (
                <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
                  {formatSmartDate(workItem.lastJobLog.createdAt)}
                </Typography>
              )}
            </Box>
          )}

        </Box>

        {/* Badges */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {partLines && partLines.length > 0 && (
              <PartsReadyBadge lines={partLines} />
            )}
            {taskLabel && (
              <Chip label={taskLabel} size="small" color={taskColor} variant="outlined" />
            )}
            {isAppointment && (
              <Tooltip title={workItem.appointmentType ?? 'Appointment'} arrow>
                <CalendarMonthIcon sx={{ fontSize: 20, color: '#1565c0' }} />
              </Tooltip>
            )}
            {onEdit && workItem.type === 'job' && (
              <Tooltip title="Edit ticket" arrow>
                <EditIcon
                  sx={{ fontSize: 16, color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                  onClick={(e) => { e.stopPropagation(); onEdit(workItem); }}
                />
              </Tooltip>
            )}
            {onPrintTicket && workItem.type === 'job' && (
              <Tooltip title="Print ticket" arrow>
                <PrintOutlinedIcon
                  sx={{ fontSize: 16, color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                  onClick={(e) => { e.stopPropagation(); onPrintTicket(workItem); }}
                />
              </Tooltip>
            )}
            {onConvertToJob && workItem.type === 'appointment' && !isCancelledAppointment && (
              <Tooltip title="Convert to job" arrow>
                <WorkOutlineIcon
                  sx={{ fontSize: 16, color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                  onClick={(e) => { e.stopPropagation(); onConvertToJob(workItem); }}
                />
              </Tooltip>
            )}
            <Chip
              label={formatStatusDisplay(workItem.status)}
              size="small"
              color={getStatusColor(workItem.status, workItem.type)}
              variant="outlined"
              onClick={onStatusChange ? (e) => {
                e.stopPropagation();
                setStatusAnchorEl(e.currentTarget);
              } : undefined}
              sx={onStatusChange ? { cursor: 'pointer' } : undefined}
            />
          </Box>
          {(() => {
            const billing = getBillingSummary(workItem);
            const text = (
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {billing.label}
              </Typography>
            );
            if (billing.details.length === 0) return text;
            return (
              <Tooltip
                title={<span style={{ whiteSpace: 'pre-line' }}>{billing.details.join('\n')}</span>}
                arrow
                placement="bottom"
              >
                {text}
              </Tooltip>
            );
          })()}
        </Box>
        {onStatusChange && (
          <Menu
            anchorEl={statusAnchorEl}
            open={Boolean(statusAnchorEl)}
            onClose={() => setStatusAnchorEl(null)}
            onClick={(e) => e.stopPropagation()}
            slotProps={{ paper: { sx: { maxHeight: 300 } } }}
          >
            {getStatusOptionsForType(workItem.type).map((option) => (
              <MenuItem
                key={option.value}
                selected={workItem.status === option.value}
                onClick={() => {
                  if (workItem.status !== option.value) {
                    onStatusChange(workItem, option.dvValue);
                  }
                  setStatusAnchorEl(null);
                }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  {workItem.status === option.value ? (
                    <CheckIcon fontSize="small" />
                  ) : (
                    <CircleIcon sx={{ fontSize: 10, color: `${option.color}.main` }} />
                  )}
                </ListItemIcon>
                <ListItemText>{option.label}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        )}
      </AccordionSummary>

      {expanded && (
        <AccordionDetails sx={{ px: 2, pb: 2 }}>
          <WorkItemExpansionPanel workItem={workItem} userName={userName} isAdmin={isAdmin} onWorkItemUpdated={onWorkItemUpdated} onDelete={onDelete} onOpenCheckInSession={onOpenCheckInSession} partLines={partLines} />
        </AccordionDetails>
      )}
    </Accordion>
    </Box>
  );
};
